import React, { useState, useEffect, useMemo } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { useGetUserGroupsQuery, useGetUserByDealerDropdownQuery } from '../../api';

/**
 * User dropdown component with multiple selection support
 * @param {Object} props
 * @param {Array} props.value - The selected values
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.dealerScoped - `true` ise `getUserDropdown` (TÜM DMS kullanıcıları) yerine
 *   `getUserByDealerDropdown` kullanılır — bayi rolündeki çağıran için yalnızca kendi bayisindeki
 *   personeli döner (Admin/WarrantyManager için fark etmez, hepsini görür). Varsayılan `false` —
 *   mevcut tüm ekranların davranışı DEĞİŞMEDEN korunur; yalnızca LMS Eğitim Talebi ekranları bunu
 *   `true` geçer (bkz. "docs/LMS EĞİTİM TALEBİ_new-changed.txt" madde 4).
 */
const UserDropdown = ({ value, onChange, className = '', disabled = false, placeholder = 'Kullanıcı seçiniz veya arayınız', dealerScoped = false }) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const { data: allUsersResponse, isLoading: isLoadingAllUsers } = useGetUserGroupsQuery(undefined, { skip: dealerScoped });
    const { data: dealerUsersResponse, isLoading: isLoadingDealerUsers } = useGetUserByDealerDropdownQuery(undefined, { skip: !dealerScoped });
    const userGroupsResponse = dealerScoped ? dealerUsersResponse : allUsersResponse;
    const isLoading = dealerScoped ? isLoadingDealerUsers : isLoadingAllUsers;

    const userOptions = useMemo(() => {
        return (
            userGroupsResponse?.data?.map((user) => ({
                id: user.id,
                name: user.name,
                displayValue: `${user.id} - ${user.name}`
            })) || []
        );
    }, [userGroupsResponse?.data]);

    // Handle selection change
    const handleSelectionChange = (e) => {
        setSelectedUsers(e.value);
        onChange(e.value);
    };

    // Set initial value if available
    useEffect(() => {
        if (value && userOptions.length > 0) {
            // Transform the incoming value format to match the dropdown options
            const selected = userOptions.filter((option) =>
                value.some((val) => {
                    if (typeof val === 'string') {
                        return val === option.id.toString();
                    }
                    if (typeof val === 'object') {
                        return val.id === option.id || val.id.toString() === option.id.toString();
                    }
                    return false;
                })
            );
            setSelectedUsers(selected);
        } else {
            setSelectedUsers([]);
        }
    }, [value, userOptions.length]);

    return (
        <MultiSelect
            value={selectedUsers}
            options={userOptions}
            onChange={handleSelectionChange}
            optionLabel="displayValue"
            filter
            showClear
            className={className}
            placeholder={placeholder}
            loading={isLoading}
            disabled={disabled}
            display="chip"
            filterBy="id,name"
            itemTemplate={(option) => (
                <div>
                    <span style={{ fontWeight: 'bold' }}>{option.id}</span> - {option.name}
                </div>
            )}
        />
    );
};

export default UserDropdown;
